import { type Route } from '@angular/router';
import { authGuard } from './shared/auth.guard';
import { redirectIfLoggedInGuard } from './shared/redirect-if-logged-in.guard';
import { orgManagedLoginLegacyRedirectRoute, orgManagedLoginRoute, orgSettingsRoutes, orgTeamViewRoute } from '@sbe-spinoff-client/spinoff-platform';
import { orgAdminAreaGuard } from './features/orgs/shared/org-admin-area.guard';
import { ORG_NAV_LABELS } from './features/orgs/shared/org-nav-labels';

// app.config.ts의 themeIgnoresStoredRoutePrefixes가 이 두 경로를 참조합니다. 리터럴을
// 양쪽에 각자 작성하면 컴파일 타임 연결이 없어 한쪽만 리네임되었을 때 조용히
// 어긋날 수 있으므로, 상수로 묶어 드리프트를 방지합니다.
export const OAUTH_AUTHORIZE_PATH = 'oauth/authorize';
export const MEMBER_PICKER_PATH = 'member-picker';

export const appRoutes: Route[] = [
  {
    // 실측 결과(2026-08-14) 모든 RP가 이 라우트를 window.open() 팝업이 아니라
    // window.location.href/서버 302 풀페이지 리다이렉트로 연다(spinoff-login.component.ts의
    // 자체 로그인, sbe-sms staff OIDC RP, 이 앱 자신의 interaction redirect, 전 e2e 흐름
    // 전수 확인 — window.open() 사용처 없음). shellBare가 아니라 일반 라우트로 둬서
    // toolbar/footer/다크모드 토글을 그대로 노출한다 — RP 화면에서 넘어온 사용자 입장에선
    // 셸 없는 화면이 오히려 어색하다.
    path: OAUTH_AUTHORIZE_PATH,
    loadComponent: () =>
      import('./features/oauth-consent/authorize/authorize.component').then(
        (m) => m.AuthorizeComponent,
      ),
  },
  {
    // 셸 밖 독립 라우트 — RP가 window.open()으로 여는 진짜 팝업이라(send.component.ts의
    // window.open()+popup.location.href, window.opener.postMessage/window.close() 사용
    // 확인됨) nav/footer가 뜨면 안 된다. 가드 없음 — 인증 자체가 없는 라우트(code 자체가
    // 자격증명, 2026-08-03 멤버 피커 팝업 플랜).
    // 컴포넌트는 popup 컨테이너(이 앱)와 iframe 컨테이너(schoolbell-e-embed-web)가 공유하는
    // lib에 있다 — 2026-08-20-member-picker-iframe-embed.md
    path: MEMBER_PICKER_PATH,
    loadComponent: () =>
      import('@sbe-spinoff-schoolbell-e/member-picker').then((m) => m.MemberPickerComponent),
    data: { shellBare: true },
  },
  // 아래 공개 라우트들과 그다음 인증 필요 그룹은 예전엔 셸용 최상위 pathless 그룹(`path: ''`,
  // canActivate 없음) 하나로 한 번 더 감싸여 있었다 — 셸 자체는 app.ts(앱 루트)가 라우트와
  // 무관하게 항상 렌더하므로 그 감싸기는 기능적으로 불필요했고, `injectProtectedEntry`
  // (콜드스타트 로딩 UI, app.ts 참고)가 최상위 배열만 보고 보호 여부를 판별하는데 그 감싸기
  // 아래 있으면 이 라우트들이 전부 최상위에서 안 보여 오분류됐다 — 그래서 최상위로
  // 평탄화했다(2026-09-24, 동작 변화 없음).
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent),
  },
  // 구 학생 로그인 주소(`login/org/<단체 id>`) — 학교가 이미 학생에게 나눠 준 안내가 깨지지 않게 새 주소(`orgs/<단체 id>/login`)로 보낸다.
  orgManagedLoginLegacyRedirectRoute(),
  {
    path: 'login',
    canActivate: [redirectIfLoggedInGuard],
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'signup',
    canActivate: [redirectIfLoggedInGuard],
    loadComponent: () =>
      import('./features/auth/signup/signup.component').then((m) => m.SignupComponent),
  },
  {
    // Kakao SDK Auth.authorize()의 고정 리다이렉트 대상 — commerce/sms와 동일 경로
    // 컨벤션(/auth/kakao-callback)이라 바꾸지 않는다. 로그인 여부와 무관하게 도달할 수
    // 있는 경로라 redirectIfLoggedInGuard는 걸지 않는다(이미 로그인된 브라우저에서 다시
    // 소셜 로그인을 시도하는 경우도 있을 수 있어 리다이렉트로 막지 않는다).
    path: 'auth/kakao-callback',
    loadComponent: () =>
      import('./features/auth/kakao-callback/kakao-callback.component').then(
        (m) => m.KakaoCallbackComponent,
      ),
  },
  {
    // 소셜 로그인(구글/애플/카카오) 인증은 됐지만 전화번호가 없는 계정의 전화번호 인증
    // 완료 화면 — pendingToken을 router state로 전달받는다(login.component.ts,
    // kakao-callback.component.ts). 로그인 전 상태이므로 authGuard 대상이 아니고,
    // redirectIfLoggedInGuard도 걸지 않는다(원칙적으로 미로그인 상태에서만 도달하지만
    // 소셜 인증 흐름 도중이라 굳이 이중 가드를 걸 필요는 없다).
    path: 'auth/social/complete',
    loadComponent: () =>
      import('./features/auth/social-complete/social-complete.component').then(
        (m) => m.SocialCompleteComponent,
      ),
  },
  {
    // 인증 필요 그룹 — 컴포넌트 없이 authGuard만 적용해 아래 children을 한 번에 보호한다.
    path: '',
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'consents',
        loadComponent: () =>
          import('./features/oauth-consent/list/consent-list.component').then(
            (m) => m.ConsentListComponent,
          ),
      },
      {
        path: 'orgs/new',
        loadComponent: () =>
          import('./features/orgs/new/org-new.component').then((m) => m.OrgNewComponent),
      },
    ],
  },
  {
    // org 컨텍스트 라우트 트리 — 누구에게나 열려 있는 이름 공간이고 인증은 그룹 단위로 건다(`docs/conventions/conventions-api-web-paths.md` §1-0).
    // 사이드바는 로컬 셸이 아니라 app.ts의 navItems()가 채우는 전역 app-shell 사이드바다(2026-08-29, ShellNavSection 도입으로 로컬
    // 2차 셸 OrgLayoutComponent를 걷어내고 평탄화). 이 라우트 자체는 컴포넌트가 없다 — 순수 경로 프리픽스 + children 전용.
    // orgId는 app.config.ts의 paramsInheritanceStrategy: 'always'로 children에 상속된다.
    path: 'orgs/:orgId',
    children: [
      // 학생 로그인 — 학교가 학생에게 나눠 주는 단체별 로그인 주소(`orgs/<단체 id>/login`)로 들어와 로그인 코드 6자를 입력한다(단체 소유 계정). 인증 그룹 밖의 형제라 가드가 없다.
      // 소유 계정은 SSO 로 다른 서비스에 넘어가지 못하고 이 앱 안에서만 쓰므로(플랜 S0 결정) 로그인 뒤에는 내 단체 목록(대시보드)으로 간다.
      orgManagedLoginRoute({ afterLoginPath: '/dashboard', title: '학생 로그인' }),
      {
        // 인증 그룹 — 컴포넌트 없이 authGuard만 적용해 아래 children을 한 번에 보호한다.
        path: '',
        canActivate: [authGuard],
        // team-only 사용자를 단체 관리 영역에서 자기 팀 뷰로 돌려보낸다 — 화면 틀만 막는 UI 가드이고
        // 인가는 각 API가 한다(팀 뷰는 data.teamScoped로 통과). org-admin-area.guard.ts 참고.
        canActivateChild: [orgAdminAreaGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./features/orgs/detail/org-detail.component').then(
                (m) => m.OrgDetailComponent,
              ),
          },
          // 팀 단위 뷰 — 앱 간 공유 화면(spinoff-platform lib: 팀 이름·계층·내 자리). `teamScoped`는 org-admin-area.guard가 읽는다.
          // 팀 멤버 패널(역할 전환)은 켜지 않는다 — schoolbell-e-api는 팀 멤버 역할 전환이 Admin↔Owner만 지원한다.
          orgTeamViewRoute({ data: { teamScoped: true } }),
          {
            path: 'teams/:teamId/service-settings',
            data: { teamScoped: true },
            loadComponent: () =>
              import('./features/orgs/team-service-settings/team-service-settings.component').then(
                (m) => m.TeamServiceSettingsComponent,
              ),
          },
          // 앱 간에 공유하는 단체 설정 화면(spinoff-platform lib) — 사이드바 라벨과 제목을 맞춘다.
          ...orgSettingsRoutes({
            auditLogTitle: ORG_NAV_LABELS.auditLog,
            membersTitle: ORG_NAV_LABELS.members,
            // Owner/Admin/Member 전원을 보여준다(2026-10-09 — 이전엔 Member만 보여주던 클라이언트 필터를 제거).
            // 멤버 추가는 전화번호만 받는다 — schoolbell-e-api는 이메일 좌석이 없고 Pending 초대 단계 없이 곧바로 좌석을 만든다.
            membersAdd: { email: false },
            // 팀 구조 — 앱 간 공유 화면(spinoff-platform lib). 학년-학급 2단계 고정(maxDepth 1), 일괄 저장 모드가 기본(변경을 모았다가 "저장"으로 `POST teams/batch` 원자 반영),
            // 처음엔 첫 학년만 펼침. org 전체 명단은 아래 `teamMembers`(`team-members`) 화면이 맡아 shared 팀 명단 라우트(`teams/:teamId/members`)는 쓰지 않는다(`roster: false`).
            teams: {
              structureTitle: ORG_NAV_LABELS.teams,
              structureEditable: true,
              structureSaveModes: true,
              structureMaxDepth: 1,
              structureCollapseInitially: true,
              structureTeamNoun: '팀',
              roster: false,
            },
            // 팀 멤버 화면 — 앱 간 공유 화면(spinoff-platform lib). 드래그 정렬·인라인 편집·일괄 저장·학생 추가 링크를 모두 공유 화면이 맡는다(플랜 Phase 3d-4).
            teamMembers: {
              title: ORG_NAV_LABELS.teamMembers,
              reorderable: true,
              editable: true,
              saveMode: 'batch',
              addStudentPath: 'managed-users',
              numberColumn: { label: '번호', valuePath: 'meta.memberNum' },
              columns: [
                { header: '본인연락처', source: 'self' },
                { header: '주보호자', source: { priority: 1 } },
                { header: '부보호자', source: { priority: 2 } },
              ],
            },
            // 멤버 분류(라벨) — 앱 간 공유 화면(spinoff-platform lib). 자리(Member) 레벨 라벨만 다루고 기본(가상) 라벨은 보이지 않는다.
            labels: { title: ORG_NAV_LABELS.labels },
            // 단체 소유(OrgManaged) 계정 관리 — 학교가 학생에게 나눠 주는 로그인 코드로만 로그인하는 계정을 이름 목록으로 만들고 코드를 인쇄·저장·재발급한다.
            managedUsers: { title: ORG_NAV_LABELS.managedUsers },
            // 서비스별 세부 권한 부여·회수 — 앱 간 공유 화면(spinoff-platform lib, schoolbell-e-api와 같은 `orgs/:orgId/permissions` 계약).
            permissions: { title: ORG_NAV_LABELS.permissions },
          }),
          {
            path: 'continuity',
            loadComponent: () =>
              import('./features/orgs/continuity/org-continuity.component').then(
                (m) => m.OrgContinuityComponent,
              ),
          },
          {
            // 서비스 바로 입장 — 다른 사이트(레거시 sbe-web-v4 서비스 메뉴)가 여는 주소. 학생·학부모(team-only)도 입장하므로
            // 단체 관리 영역 가드를 통과시킨다(`teamScoped`). `?teamId=`가 있으면 팀 단위 입장이다.
            path: 'services/:clientId/enter',
            data: { teamScoped: true },
            loadComponent: () =>
              import('./features/orgs/service-enter/service-enter.component').then(
                (m) => m.ServiceEnterComponent,
              ),
          },
          {
            path: 'services/:clientId',
            loadComponent: () =>
              import('./features/orgs/service-detail/service-detail.component').then(
                (m) => m.ServiceDetailComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];
